
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormMessage 
} from "@/components/ui/form";
import { useAuth } from "@/hooks/useAuth";
import { Alert, AlertDescription } from "@/components/ui/alert";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(8, "Password must be at least 8 characters long"),
});

type LoginFormValues = z.infer<typeof loginSchema>;

interface LoginFormProps {
  onForgotPassword: () => void;
  onLoginSuccess?: (result: any) => void;
}

const LoginForm = ({ onForgotPassword, onLoginSuccess }: LoginFormProps) => {
  const { signIn } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState<string | null>(null);
  
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const isSubmitting = form.formState.isSubmitting;

  const handleSubmit = async (values: LoginFormValues) => {
    try {
      setLoginError(null); // Clear previous errors
      console.log("Attempting login with:", values.email);
      
      const result = await signIn(values.email, values.password);
      
      if (result.error) {
        console.log("Login error occurred:", result.error.message);
        
        // Handle specific error cases
        if (result.error.message.includes("Invalid login credentials")) {
          console.log("Invalid credentials for:", values.email);
          setLoginError("Incorrect email or password. Please try again.");
          toast({
            title: "Login failed",
            description: "Incorrect email or password. Please try again.",
            variant: "destructive",
          });
        } else {
          setLoginError(result.error.message || "An error occurred during login.");
          toast({
            title: "Login failed",
            description: result.error.message || "An error occurred during login.",
            variant: "destructive",
          });
        }
        return;
      } 
      
      console.log("Login successful! Result:", result);
      
      toast({
        title: "Welcome back!",
        description: "You have successfully logged in",
      });
      
      // Call the onLoginSuccess callback if provided
      if (onLoginSuccess) {
        onLoginSuccess(result);
        return;
      }
      
      // If no callback is provided, handle success here
      // Use navigate instead of direct location change for smoother experience
      navigate("/");
    } catch (error) {
      console.error("Login error:", error);
      setLoginError("An unexpected error occurred. Please try again.");
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="grid gap-4">
        {loginError && (
          <Alert className="bg-red-900/30 border-red-800 text-red-100">
            <AlertDescription>{loginError}</AlertDescription>
          </Alert>
        )}
        
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="grid gap-2">
              <FormControl>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="name@example.com"
                  className="bg-gray-800 border-gray-700"
                  aria-invalid={!!form.formState.errors.email}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className="grid gap-2">
              <div className="flex items-center justify-between">
                <FormControl>
                  <Input
                    id="login-password"
                    type="password"
                    className="bg-gray-800 border-gray-700"
                    aria-invalid={!!form.formState.errors.password}
                    {...field}
                  />
                </FormControl>
                <Button 
                  type="button" 
                  variant="link" 
                  className="text-sm text-tokscript-cta hover:underline"
                  onClick={onForgotPassword}
                >
                  Forgot password?
                </Button>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <Button 
          type="submit"
          className="bg-tokscript-cta hover:bg-tokscript-cta/90 text-black"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Logging in...
            </>
          ) : (
            "Log In"
          )}
        </Button>
      </form>
    </Form>
  );
};

export default LoginForm;
